import { CSSProperties, RefObject, useCallback, useLayoutEffect, useState } from 'react';

interface PopoverOptions {
  align?: 'start' | 'end';
  matchWidth?: boolean;
  offset?: number;
  deps?: unknown[];
}

/**
 * Positions a floating panel (rendered in a portal) next to its anchor with
 * position: fixed, so it never gets clipped by modals or scroll containers.
 * Flips above the anchor when there isn't room below.
 */
export function usePopover(
  open: boolean,
  anchorRef: RefObject<HTMLElement>,
  popRef: RefObject<HTMLElement>,
  { align = 'start', matchWidth = false, offset = 6, deps = [] }: PopoverOptions = {},
): CSSProperties {
  const [style, setStyle] = useState<CSSProperties>({ position: 'fixed', top: -9999, left: -9999 });

  const update = useCallback(() => {
    const a = anchorRef.current;
    const p = popRef.current;
    if (!a || !p) return;
    const r = a.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const pw = Math.max(p.offsetWidth, matchWidth ? r.width : 0);
    const ph = p.offsetHeight;
    let top = r.bottom + offset;
    if (top + ph > vh - 8 && r.top - offset - ph > 8) top = r.top - offset - ph;
    let left = align === 'end' ? r.right - pw : r.left;
    left = Math.max(8, Math.min(left, vw - pw - 8));
    setStyle({ position: 'fixed', top, left, minWidth: matchWidth ? r.width : undefined, zIndex: 60 });
  }, [anchorRef, popRef, align, matchWidth, offset]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, update, ...deps]);

  return style;
}
